'use client';

import type { Schema } from 'effect';
import type { ReactNode } from 'react';
import { Option } from 'effect';

import { fetchBrandsByPageAtom } from '../../../../effect/atom/brand/fetchBrandsByPage';
import { getBrandsAlphabetAtom } from '../../../../effect/atom/brand/getBrandsAlphabet';
import {
  alphabetFilterSearchParamAtom,
  pageSearchParamAtom,
  searchFilterSearchParamAtom,
} from '@vs-shop/engine/atom/brand/pagination';
import type { BrandAlphabet, BrandIndex } from '../../../../effect/schema/openapi/backend';
import type { HydraCollectionResponseType } from '@vs-shop/schemas/openapi/response';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomInitialValues } from '@effect/atom-react';

type Props = {
  page: number;
  alphabetFilter: string | null;
  searchFilter: string | null;
  brandsResponse: HydraCollectionResponseType<typeof BrandIndex>;
  alphabetResponse: Schema.Schema.Type<typeof BrandAlphabet>;
  children: ReactNode;
};

export default function BrandsContextInitializer({
  page,
  alphabetFilter,
  searchFilter,
  brandsResponse,
  alphabetResponse,
  children,
}: Props) {
  useAtomInitialValues([
    // Initialize brands data for current page with filters
    [
      fetchBrandsByPageAtom(page, alphabetFilter, searchFilter),
      AsyncResult.success(brandsResponse),
    ],
    // Initialize alphabet data
    [getBrandsAlphabetAtom, AsyncResult.success(alphabetResponse)],
    // Initialize URL params
    [pageSearchParamAtom, page === 1 ? Option.none() : Option.some(page)],
    [
      alphabetFilterSearchParamAtom,
      alphabetFilter !== null ? Option.some(alphabetFilter) : Option.none(),
    ],
    [
      searchFilterSearchParamAtom,
      searchFilter !== null ? Option.some(searchFilter) : Option.none(),
    ],
  ]);

  return <>{children}</>;
}
